// Lightweight per-page SEO hook.
// This is a client-side-rendered CRA app with a single index.html shell, so
// non-JS crawlers (most social unfurlers, most AI/LLM crawlers) only ever
// see index.html's static tags — this hook only helps crawlers that execute
// JS (Googlebot) plus the browser tab/bookmark UX. Mirrors what react-helmet
// does internally, scoped to just the fields this project needs, so we don't
// need the dependency.

import { useEffect } from "react";

const SITE_URL = "https://plentyofhearts.com";

interface SEOConfig {
  title: string;
  description: string;
  /** Path only, e.g. "/privacy-policy" — combined with SITE_URL. */
  canonical?: string;
}

export const useSEO = ({ title, description, canonical }: SEOConfig): void => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const descriptionTag = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionTag?.getAttribute("content") ?? "";
    descriptionTag?.setAttribute("content", description);

    const ogTitleTag = document.querySelector('meta[property="og:title"]');
    const previousOgTitle = ogTitleTag?.getAttribute("content") ?? "";
    ogTitleTag?.setAttribute("content", title);

    const ogDescriptionTag = document.querySelector('meta[property="og:description"]');
    const previousOgDescription = ogDescriptionTag?.getAttribute("content") ?? "";
    ogDescriptionTag?.setAttribute("content", description);

    const canonicalTag = document.querySelector('link[rel="canonical"]');
    const previousCanonicalHref = canonicalTag?.getAttribute("href") ?? `${SITE_URL}/`;
    if (canonical) {
      canonicalTag?.setAttribute("href", `${SITE_URL}${canonical}`);
    }

    return () => {
      document.title = previousTitle;
      descriptionTag?.setAttribute("content", previousDescription);
      ogTitleTag?.setAttribute("content", previousOgTitle);
      ogDescriptionTag?.setAttribute("content", previousOgDescription);
      if (canonical) {
        canonicalTag?.setAttribute("href", previousCanonicalHref);
      }
    };
  }, [title, description, canonical]);
};
